import { Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AlertService } from 'src/app/components/alert/alert.service';
import { ESearchType, ISearchEvent } from 'src/app/components/search-form/search-form.constants';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { IAnagrafica } from 'src/app/modules/shared/components/ndg-variation/ndg-variation.constants';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import { EPolicyStatus, IPoliciesResponse, IPolicy, IPolicyFilter, ISubject, ISubjectRequest, ISubjectResponse } from 'src/app/modules/shared/models/policy.model';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { ANAGRAFICHE_MAX_LENGTH, ANAGRAFICHE_WARN_MESSAGE, POLICIES_ACCREDITAMENTO_WARN_MESSAGE, policy_search_keys, policy_types_filter, subject_search_keys_PF, subject_search_keys_PG, SUBJECTS_ACCREDITAMENTO_WARN_MESSAGE } from './search.constants';
import { SearchService } from './search.service';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrl: './search.component.scss',
})
export class SearchComponent implements OnInit, OnDestroy {

  polizze: IPolicy[] = [];
  anagrafiche: ISubject[] = [];
  userSelected: ISubject = null;
  policyKeys: IFormKey[] = CommonUtils.assign(policy_search_keys);
  policyTypesFilter: IPolicyFilter[] = policy_types_filter;
  activePolicyTypeFilter: string[] = null;
  notYetSearched = true;
  isAdvancedSearch = false;
  resultsTotalCount = 0;
  event: ISearchEvent;
  destroy$ = new Subject();

  constructor(
    private readonly _router: Router,
    private readonly _location: Location,
    private readonly _spinner: SpinnerService,
    private readonly _searchService: SearchService,
    private readonly _alertService: AlertService,
    private _authenticationService: AuthenticationService,
    private _policyService: PolicyService
  ) { }

  ngOnInit(): void {
    localStorage.removeItem('movementId');
    this.getNavigationState();
  }

  getUserName(user: ISubject): string {
    if (user) {
      const name = user.type === 'PF' ? `${user.name} ${user.surname}` : user.businessName;
      return name;
    }
    return '';
  }

  getSubjectKeys(user: ISubject): IFormKey[] {
    if (user) {
      return user.type === 'PF' ? CommonUtils.assign(subject_search_keys_PF) : CommonUtils.assign(subject_search_keys_PG);
    }
    return [];
  }

  getNavigationState() {
    const state = this._location.getState() as ISearchEvent;
    LoggerService.logInfo(this.constructor.name, 'getNavigationState', { state });
    if (state && state.type && state.rawForm) {
      this.event = state;
      this.onSearchRequested(state);
    }
  }

  onTabChange(index: number) {
    LoggerService.logInfo(this.constructor.name, 'onTabChange', { index });
    this.notYetSearched = true;
    this.isAdvancedSearch = index > 0;
  }

  onPolicySelected(itemEvent: ISubject | IPolicy | IAnagrafica) {
    const item = itemEvent as IPolicy
    LoggerService.logInfo(this.constructor.name, 'onPolicySelected', { item });
    this._policyService.contractNumber = item?.bperContractNumber;
    this._router.navigate([`/policies/${item?.bperContractNumber}`]);
  }

  onSubjectSelected(subjectEvent: ISubject | IPolicy | IAnagrafica) {
    const subject = subjectEvent as ISubject;
    LoggerService.logInfo(this.constructor.name, 'onSubjectSelected', { subject });
    this.userSelected = subject;
    this.clearPreviousResults();
    this.basicSearch(subject?.ndg, null, null);
  }

  onSearchRequested(event: ISearchEvent) {
    LoggerService.logInfo(this.constructor.name, 'onSearchRequested', { event });
    this.clearPreviousResults();
    switch (event.type) {
      case ESearchType.BASIC:
        this.basicSearch(CommonUtils.checkAndGet(event, 'rawForm.ndg', null), null, null);
        break;
      case ESearchType.ADVANCED:
        const skipSubjectList: boolean = CommonUtils.checkAndGet(event, 'skipSubjectList', false);
        this.advancedSearch(CommonUtils.checkAndGet(event, 'rawForm', null), skipSubjectList);
        break;
      default:
        break;
    }
  }

  onPolicyTypeFilterClick(policyFilter: IPolicyFilter) {
    LoggerService.logInfo(this.constructor.name, 'onPolicyTypeFilterClick', policyFilter);
    if (this.activePolicyTypeFilter?.includes(CommonUtils.checkAndGet(policyFilter, 'label', null))) {
      this.activePolicyTypeFilter = null;
      this.policyTypesFilter = this.policyTypesFilter.map(policyFilter => ({...policyFilter, currentStyle: policyFilter.originalStyle}));
    } else {
      this.activePolicyTypeFilter = CommonUtils.checkAndGet(policyFilter, 'policyTypes', null);
      this.policyTypesFilter = this.policyTypesFilter.map(policyFilter => (!this.activePolicyTypeFilter?.includes(policyFilter.label) ? {...policyFilter, currentStyle: 'badge-disabled'} : {...policyFilter, currentStyle: policyFilter.originalStyle}));
    }
  }

  clearPreviousResults() {
    this.polizze = CommonUtils.assign([]);
    this.anagrafiche = CommonUtils.assign([]);
    this.userSelected = CommonUtils.assign(null);
    this.notYetSearched = true;
  }

  basicSearch(ndg: string, policyNumber: string, contractNumber: string) {
    if (!CommonUtils.isVoid(ndg)) {
      this._spinner.show();
      this._searchService.getPoliciesByNdg(ndg, policyNumber, contractNumber)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response: IPoliciesResponse) => {
            LoggerService.logInfo(this.constructor.name, 'basicSearch', { response });
            this.notYetSearched = false;
            this.isAdvancedSearch = false;
            this.polizze = CommonUtils.checkAndGet(response, 'list', null);
            const user =  CommonUtils.checkAndGet(response, 'subject', null);
            this.userSelected = !CommonUtils.isVoidObj(user) ? user : null;
            this._spinner.hide();
          },
          error: error => {
            LoggerService.logError(this.constructor.name, 'basicSearch', { error });
            this._alertService.openErrorMessage('Errore nel recupero delle polizze', 'basicSearch', error);
            this._spinner.hide();
          }
        })
    }
  }

  advancedSearch(rawForm: ISubjectRequest, skipSubjectList: boolean) {
    this._spinner.show();
    this._searchService.getSubject(rawForm)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: ISubjectResponse) => {
          LoggerService.logInfo(this.constructor.name, 'advancedSearch', { response });
          this.notYetSearched = false;
          this.isAdvancedSearch = true;
          if (skipSubjectList && response && response.subjects) {
            const ndg = CommonUtils.checkAndGet(response.subjects[0], 'ndg', null);
            const policyNumber: string = CommonUtils.checkAndGet(rawForm, 'policyId', null);
            const contractNumber: string = CommonUtils.checkAndGet(rawForm, 'contractId', null);
            if (ndg && policyNumber) {
              this.basicSearch(ndg, policyNumber, null);
            } else if (ndg && contractNumber) {
              this.basicSearch(ndg, null, contractNumber);
            }
          } else {
            this.anagrafiche = CommonUtils.checkAndGet(response, 'subjects', null);
            this.resultsTotalCount = CommonUtils.checkAndGet(response, 'totalCount', null);
          }
          this._spinner.hide();
        },
        error: (error) => {
          LoggerService.logError(this.constructor.name, 'advancedSearch', { error });
          this._alertService.openErrorMessage('Errore nel recupero dei soggetti', 'advancedSearch', error);
          this._spinner.hide();
        }
      })
  }

  showArrow(item): boolean {
    return CommonUtils.checkAndGet(item, 'policyStatus', null) !== EPolicyStatus.TRASFERITA;
  }

  get filteredPolicies(): IPolicy[] {
    if (this.activePolicyTypeFilter) {
      return this.polizze.filter(
        policy => (this.activePolicyTypeFilter?.includes(policy.policyType)));
    } else {
      return this.polizze;
    }
  }

  get showResultsMessage(): boolean {
    return this.resultsTotalCount > ANAGRAFICHE_MAX_LENGTH;
  }

  get showAccreditamentoMessage(): boolean {
    return CommonUtils.checkAndGet(this._authenticationService.loggedUser, 'isCesarePontiInBper', null) &&
           !CommonUtils.checkAndGet(this._authenticationService.loggedUser, 'isAccreditato', null);
  }

  get anagraficheWarnMessage(): string {
    return ANAGRAFICHE_WARN_MESSAGE;
  }

  get subjectsAccreditamentoWarnMessage(): string {
    return SUBJECTS_ACCREDITAMENTO_WARN_MESSAGE;
  }

  get policiesAccreditamentoWarnMessage(): string {
    return POLICIES_ACCREDITAMENTO_WARN_MESSAGE;
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }

}
